"use client";

import React, { useMemo, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { Menu, X } from "lucide-react";
import { useCasinoPage } from "../CasinoPageContext";

const ogIcon = new URL("@/app/assets/og-icon.png", import.meta.url).href;

interface NavLink {
  name: string;
  href: string;
}

interface LinkItemProps {
  href: string;
  name: string;
  isMobile: boolean;
  onClick?: () => void;
}

const Navbar = () => {
  const { website } = useCasinoPage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  const [mobileGoToOpen, setMobileGoToOpen] = useState<boolean>(false);
  const [desktopGoToOpen, setDesktopGoToOpen] = useState<boolean>(false);

  const sectionLinks: NavLink[] = [
    { name: "About", href: "/#about-section" },
    { name: "Blogs", href: "/#blogs-section" },
    { name: "FAQs", href: "/#faq-section" },
  ];

  const navLinks: NavLink[] = useMemo(() => {
    const subpages = website?.subpages ?? [];

    return subpages.map((subpage) => ({
      name: subpage.name,
      href: `/${encodeURIComponent(subpage.slug)}`,
    }));
  }, [website]);

  const LinkItem = ({ href, name, isMobile, onClick }: LinkItemProps) => (
    <Link
      href={href}
      passHref
      className={`
          hover:text-yellow-400 transition-colors text-white font-normal 
          ${isMobile ? "block py-3 text-lg" : "text-base"}
        `}
      onClick={onClick}
    >
      {name}
    </Link>
  );

  return (
    <nav className="bg-gray-900/50 backdrop-blur-md shadow-xl fixed w-full top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 text-white">
          {/* Logo Section */}
          <div className="flex-shrink-0">
            <Link
              href={`/`}
              passHref
              className="inline-flex items-center gap-2 hover:text-yellow-400 transition-colors"
              aria-label="CasinoB8 homepage"
            >
              <span className="relative h-8 w-8 rounded-full overflow-hidden">
                <Image
                  src={ogIcon}
                  alt="CasinoB8 logo"
                  fill
                  sizes="32px"
                  className="object-contain"
                  priority
                />
              </span>
              <span className="text-2xl md:text-3xl font-extrabold tracking-wider">
                CasinoB8
              </span>
            </Link>
          </div>

          {/* Desktop Links */}
          <div className="hidden md:flex items-center space-x-8">
            {sectionLinks.map((link) => (
              <LinkItem
                key={link.name}
                href={link.href}
                name={link.name}
                isMobile={false}
              />
            ))}

            {/* Go to dropdown for subpages */}
            {navLinks.length > 0 && (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setDesktopGoToOpen((open) => !open)}
                  className="text-base font-normal text-white hover:text-yellow-400 transition-colors inline-flex items-center gap-1"
                  aria-haspopup="true"
                  aria-expanded={desktopGoToOpen}
                >
                  Go to
                  <span className="text-xs">▾</span>
                </button>
                <div
                  className={`absolute right-0 mt-2 w-56 rounded-xl bg-gray-900/95 border border-gray-700/80 shadow-xl transition-all duration-200 z-50
                    ${
                      desktopGoToOpen
                        ? "opacity-100 translate-y-0"
                        : "opacity-0 translate-y-2 pointer-events-none"
                    }
                  `}
                >
                  <ul className="py-2 text-sm text-gray-100">
                    {navLinks.map((link) => (
                      <li key={link.name}>
                        <Link
                          href={link.href}
                          className="block px-4 py-2 hover:bg-gray-800/80 hover:text-yellow-400 transition-colors"
                          onClick={() => setDesktopGoToOpen(false)}
                        >
                          {link.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* Mobile Menu Button*/}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden text-white text-3xl p-2 rounded focus:outline-none focus:ring-2 focus:ring-yellow-400"
            aria-label="Toggle navigation menu"
          >
            {mobileMenuOpen ? <X size={30} /> : <Menu size={30} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Content */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-gray-800 border-t border-gray-700">
          <div className="px-4 py-3 space-y-1">
            {/* Section anchors */}
            {sectionLinks.map((link) => (
              <LinkItem
                key={link.name}
                href={link.href}
                name={link.name}
                isMobile={true}
                onClick={() => setMobileMenuOpen(false)}
              />
            ))}

            {/* Go to dropdown for subpages (mobile) */}
            {navLinks.length > 0 && (
              <div className="pt-2">
                <button
                  type="button"
                  onClick={() => setMobileGoToOpen((open) => !open)}
                  className="w-full text-left text-white font-normal text-lg py-3 flex items-center justify-between"
                  aria-haspopup="true"
                  aria-expanded={mobileGoToOpen}
                >
                  <span>Go to</span>
                  <span className="text-sm">{mobileGoToOpen ? "▴" : "▾"}</span>
                </button>
                {mobileGoToOpen && (
                  <ul className="pl-3 pb-2 space-y-1">
                    {navLinks.map((link) => (
                      <li key={link.name}>
                        <Link
                          href={link.href}
                          className="block py-2 text-base text-gray-200 hover:text-yellow-400"
                          onClick={() => {
                            setMobileMenuOpen(false);
                            setMobileGoToOpen(false);
                          }}
                        >
                          {link.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
